import { useMemo, useState, useEffect } from "react";
import { Bell, PackageX, AlertCircle, X, IdCard, FileCheck, CheckCircle2, FileText, TrendingUp, ShoppingCart, Ban, Timer, Bug, DollarSign, PackagePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useNotificaciones } from "@/hooks/useNotificaciones";
import { useNavigate } from "react-router-dom";

interface DismissedNotification {
  id: string;
  dismissedAt: number;
}

const STORAGE_KEY = "dismissed-notifications";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const CentroNotificaciones = () => {
  const { alertasCaducidad, notificacionesStock, alertasLicencias, autorizacionesOC, autorizacionesCotizacion, confirmacionesProveedor, notificacionesPrecios, notificacionesPedidos, notificacionesRechazo, notificacionesCaducidadPush, notificacionesFumigacionPush, notificacionesPreciosVendedor, notificacionesProductoNuevo, totalCount, loading, marcarComoLeida, marcarTodasComoLeidas, isAdmin } = useNotificaciones();
  const navigate = useNavigate();
  const [dismissedLicencias, setDismissedLicencias] = useState<string[]>([]);
  const [dismissedCaducidad, setDismissedCaducidad] = useState<string[]>([]);
  const [dismissedConfirmaciones, setDismissedConfirmaciones] = useState<string[]>([]);

  // Cargar notificaciones descartadas del localStorage al montar
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const dismissed: DismissedNotification[] = JSON.parse(stored);
        const now = Date.now();
        
        // Filtrar las que aún no han pasado 24 horas
        const stillValid = dismissed.filter(d => (now - d.dismissedAt) < ONE_DAY_MS);
        
        // Actualizar localStorage con las válidas
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stillValid));
        
        // Separar por tipo (L = licencias, C = caducidad, P = confirmaciones proveedor)
        const licencias = stillValid.filter(d => d.id.startsWith("L-")).map(d => d.id);
        const caducidad = stillValid.filter(d => d.id.startsWith("C-")).map(d => d.id);
        const confirmaciones = stillValid.filter(d => d.id.startsWith("P-")).map(d => d.id);
        
        setDismissedLicencias(licencias);
        setDismissedCaducidad(caducidad);
        setDismissedConfirmaciones(confirmaciones);
      }
    } catch (error) {
      console.error("Error loading dismissed notifications:", error);
    }
  }, []);

  // Guardar en localStorage cuando cambian las notificaciones descartadas
  useEffect(() => {
    try {
      const now = Date.now();
      const allDismissed: DismissedNotification[] = [
        ...dismissedLicencias.map(id => ({ id, dismissedAt: now })),
        ...dismissedCaducidad.map(id => ({ id, dismissedAt: now })),
        ...dismissedConfirmaciones.map(id => ({ id, dismissedAt: now }))
      ];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allDismissed));
    } catch (error) {
      console.error("Error saving dismissed notifications:", error);
    }
  }, [dismissedLicencias, dismissedCaducidad, dismissedConfirmaciones]);

  const visibleAlertasLicencias = useMemo(
    () => alertasLicencias.filter((a) => !dismissedLicencias.includes(`L-${a.id}`)),
    [alertasLicencias, dismissedLicencias]
  );

  const visibleAlertasCaducidad = useMemo(
    () => alertasCaducidad.filter((a) => !dismissedCaducidad.includes(`C-${a.id}`)),
    [alertasCaducidad, dismissedCaducidad]
  );

  const visibleConfirmaciones = useMemo(
    () => confirmacionesProveedor.filter((c) => !dismissedConfirmaciones.includes(`P-${c.id}`)),
    [confirmacionesProveedor, dismissedConfirmaciones]
  );

  const computedCount = notificacionesStock.length + visibleAlertasLicencias.length + visibleAlertasCaducidad.length + autorizacionesOC.length + autorizacionesCotizacion.length + visibleConfirmaciones.length + notificacionesPrecios.length + notificacionesPedidos.length + notificacionesRechazo.length + notificacionesCaducidadPush.length + notificacionesFumigacionPush.length + notificacionesPreciosVendedor.length + notificacionesProductoNuevo.length;

  const handleLicenciaClick = (puesto: string) => {
    const tabMap: Record<string, string> = {
      "Chofer": "chofer",
      "Vendedor": "vendedor",
    };
    const tab = tabMap[puesto] || "todos";
    navigate(`/empleados?tab=${tab}`);
  };

  const handleCaducidadClick = () => {
    navigate("/inventario");
  };

  const handleAutorizacionClick = (ordenCompraId: string, notificacionId: string) => {
    marcarComoLeida(notificacionId);
    navigate(`/compras?aprobar=${ordenCompraId}`);
  };

  const handleAutorizacionCotizacionClick = (cotizacionId: string, notificacionId: string) => {
    marcarComoLeida(notificacionId);
    navigate(`/pedidos?aprobar_cotizacion=${cotizacionId}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          {computedCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 min-w-5 flex items-center justify-center px-1 text-xs"
            >
              {computedCount > 99 ? "99+" : computedCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">Notificaciones</h3>
          <div className="flex items-center gap-2">
            {computedCount > 0 && (
              <>
                <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => marcarTodasComoLeidas()}>
                  <CheckCircle2 className="h-3 w-3 mr-1" />Limpiar
                </Button>
                <Badge variant="secondary">{computedCount}</Badge>
              </>
            )}
          </div>
        </div>

        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Cargando notificaciones...
            </div>
          ) : computedCount === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No hay notificaciones</p>
            </div>
          ) : (
            <div className="p-2">
              {/* Autorizaciones de Órdenes de Compra - Prioritarias */}
              {autorizacionesOC.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Autorizaciones Pendientes
                  </div>
                  {autorizacionesOC.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 mb-2"
                      onClick={() => handleAutorizacionClick(notif.orden_compra_id, notif.id)}
                    >
                      <FileCheck className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notif.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                        Revisar
                      </Badge>
                    </div>
                  ))}
                  {(autorizacionesCotizacion.length > 0 || visibleConfirmaciones.length > 0 || notificacionesStock.length > 0 || visibleAlertasLicencias.length > 0 || visibleAlertasCaducidad.length > 0) && (
                    <Separator className="my-2" />
                  )}
                </div>
              )}

              {/* Autorizaciones de Cotizaciones - Prioritarias */}
              {autorizacionesCotizacion.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Cotizaciones Pendientes de Autorización
                  </div>
                  {autorizacionesCotizacion.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800 mb-2"
                      onClick={() => handleAutorizacionCotizacionClick(notif.cotizacion_id, notif.id)}
                    >
                      <FileText className="h-5 w-5 text-purple-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.folio}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          Cotización para {notif.cliente_nombre}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                        Revisar
                      </Badge>
                    </div>
                  ))}
                  {(visibleConfirmaciones.length > 0 || notificacionesStock.length > 0 || visibleAlertasLicencias.length > 0 || visibleAlertasCaducidad.length > 0) && (
                    <Separator className="my-2" />
                  )}
                </div>
              )}

              {/* Rechazos de Entrega */}
              {notificacionesRechazo.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Entregas Rechazadas
                  </div>
                  {notificacionesRechazo.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 mb-2"
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate('/compras?tab=devoluciones-faltantes');
                      }}
                    >
                      <Ban className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notif.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                        Rechazada
                      </Badge>
                    </div>
                  ))}
                  <Separator className="my-2" />
                </div>
              )}

              {/* Confirmaciones de Proveedores */}
              {visibleConfirmaciones.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Órdenes Confirmadas por Proveedor
                  </div>
                  {visibleConfirmaciones.map((conf) => (
                    <div
                      key={conf.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 mb-2 group"
                      onClick={() => navigate(`/compras?orden=${conf.orden_compra_id}`)}
                    >
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{conf.folio}</p>
                        <p className="text-xs text-muted-foreground">
                          {conf.proveedor_nombre} confirmó esta orden
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(conf.confirmado_en).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
                          Confirmada
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDismissedConfirmaciones((prev) => [...prev, `P-${conf.id}`]);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(notificacionesStock.length > 0 || visibleAlertasLicencias.length > 0 || visibleAlertasCaducidad.length > 0) && (
                    <Separator className="my-2" />
                  )}
                </div>
              )}

              {/* Notificaciones de Precios */}
              {notificacionesPrecios.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Revisión de Precios
                  </div>
                  {notificacionesPrecios.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800 mb-2"
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate('/precios');
                      }}
                    >
                      <TrendingUp className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notif.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300">
                        Revisar
                      </Badge>
                    </div>
                  ))}
                  <Separator className="my-2" />
                </div>
              )}

              {/* Notificaciones de Nuevos Pedidos */}
              {notificacionesPedidos.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Nuevos Pedidos
                  </div>
                  {notificacionesPedidos.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer mb-2"
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate('/pedidos');
                      }}
                    >
                      <ShoppingCart className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notif.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <Separator className="my-2" />
                </div>
              )}

              {/* Precios Actualizados (Vendedor) */}
              {notificacionesPreciosVendedor.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Precios Actualizados
                  </div>
                  {notificacionesPreciosVendedor.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 mb-2"
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate('/precios');
                      }}
                    >
                      <DollarSign className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notif.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700">
                        Precio
                      </Badge>
                    </div>
                  ))}
                  <Separator className="my-2" />
                </div>
              )}

              {/* Producto Nuevo (Vendedor) */}
              {notificacionesProductoNuevo.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Nuevos Productos
                  </div>
                  {notificacionesProductoNuevo.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 mb-2"
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate('/productos');
                      }}
                    >
                      <PackagePlus className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notif.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                      <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700">
                        Nuevo
                      </Badge>
                    </div>
                  ))}
                  <Separator className="my-2" />
                </div>
              )}

              {/* Alertas de Caducidad (Push) */}
              {notificacionesCaducidadPush.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Alertas de Caducidad
                  </div>
                  {notificacionesCaducidadPush.map((notif) => (
                    <div
                      key={notif.id}
                      className={`flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer mb-2 border ${
                        notif.tipo === 'caducidad_vencida' 
                          ? 'bg-destructive/10 dark:bg-destructive/20 border-destructive/30' 
                          : 'bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800'
                      }`}
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate('/almacen-tablet');
                      }}
                    >
                      <Timer className={`h-5 w-5 mt-0.5 flex-shrink-0 ${notif.tipo === 'caducidad_vencida' ? 'text-destructive' : 'text-orange-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">{notif.descripcion}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <Badge variant="outline" className={notif.tipo === 'caducidad_vencida' ? 'bg-destructive/20 text-destructive border-destructive/30' : 'bg-orange-100 text-orange-700 border-orange-300'}>
                        {notif.tipo === 'caducidad_vencida' ? 'Vencido' : 'Crítico'}
                      </Badge>
                    </div>
                  ))}
                  <Separator className="my-2" />
                </div>
              )}

              {/* Alertas de Fumigación (Push) */}
              {notificacionesFumigacionPush.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Alertas de Fumigación
                  </div>
                  {notificacionesFumigacionPush.map((notif) => (
                    <div
                      key={notif.id}
                      className={`flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer mb-2 border ${
                        notif.tipo === 'fumigacion_vencida' 
                          ? 'bg-destructive/10 dark:bg-destructive/20 border-destructive/30' 
                          : 'bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800'
                      }`}
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate('/almacen-tablet');
                      }}
                    >
                      <Bug className={`h-5 w-5 mt-0.5 flex-shrink-0 ${notif.tipo === 'fumigacion_vencida' ? 'text-destructive' : 'text-yellow-600'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">{notif.descripcion}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                      <Badge variant="outline" className={notif.tipo === 'fumigacion_vencida' ? 'bg-destructive/20 text-destructive border-destructive/30' : 'bg-yellow-100 text-yellow-700 border-yellow-300'}>
                        {notif.tipo === 'fumigacion_vencida' ? 'Vencida' : 'Próxima'}
                      </Badge>
                    </div>
                  ))}
                  <Separator className="my-2" />
                </div>
              )}

              {/* Notificaciones de Stock Bajo */}
              {notificacionesStock.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Stock Bajo
                  </div>
                  {notificacionesStock.map((notif) => (
                    <div
                      key={notif.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => {
                        marcarComoLeida(notif.id);
                        navigate("/productos");
                      }}
                    >
                      <PackageX className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{notif.titulo}</p>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {notif.descripcion}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(notif.created_at).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                  {alertasCaducidad.length > 0 && <Separator className="my-2" />}
                </div>
              )}

              {/* Alertas de Licencias */}
              {visibleAlertasLicencias.length > 0 && (
                <div className="mb-2">
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Licencias de Conductor
                  </div>
                  {visibleAlertasLicencias.map((alerta) => (
                    <div
                      key={alerta.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                    >
                      <IdCard 
                        className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                          alerta.vencida ? "text-red-500" : "text-yellow-500"
                        }`}
                      />
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => handleLicenciaClick(alerta.empleado_puesto)}
                      >
                        <p className="text-sm font-medium">
                          {alerta.empleado_nombre}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {alerta.empleado_puesto} • {" "}
                          {alerta.vencida 
                            ? `Vencida hace ${Math.abs(alerta.dias_restantes)} ${Math.abs(alerta.dias_restantes) === 1 ? "día" : "días"}`
                            : `Vence en ${alerta.dias_restantes} ${alerta.dias_restantes === 1 ? "día" : "días"}`
                          }
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(alerta.fecha_vencimiento).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDismissedLicencias((prev) => [...prev, `L-${alerta.id}`]);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {visibleAlertasCaducidad.length > 0 && <Separator className="my-2" />}
                </div>
              )}

              {/* Alertas de Caducidad */}
              {visibleAlertasCaducidad.length > 0 && (
                <div>
                  <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">
                    Próximos a Caducar
                  </div>
                  {visibleAlertasCaducidad.map((alerta) => (
                    <div
                      key={alerta.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                    >
                      <AlertCircle 
                        className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                          alerta.dias_restantes <= 7 ? "text-red-500" : "text-yellow-500"
                        }`}
                      />
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={handleCaducidadClick}
                      >
                        <p className="text-sm font-medium">
                          {alerta.producto_codigo} - {alerta.producto_nombre}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {alerta.lote && `Lote: ${alerta.lote} • `}
                          Caduca en {alerta.dias_restantes} {alerta.dias_restantes === 1 ? "día" : "días"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(alerta.fecha_caducidad).toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDismissedCaducidad((prev) => [...prev, `C-${alerta.id}`]);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
